// THE QUEST PICTURE PROFILE (quest-steady72, 2026-11-03; APP_PLAYER "The Quest picture profile",
// APP_SURFACE_PERF "quest-steady72"): the owner picks, in the headset (VR ☰ menu → Picture) or by URL
// (`?qp=current|balanced|steady`), how the Quest trades edge smoothing against frame rate. Only the
// Quest class has profiles (device.js deviceClass() 'quest'); every other class: questProfile() = null
// and nothing here changes a thing.
//
// Why (the owner's telemetry 2026-11-03 07:00–07:05 + w24's headset arms at the heavy town view):
// at 4x MSAA the eye pass costs 15.9–18.4 ms App GPU at scale 0.6–0.75 → 36 fps (each frame shown
// twice, rotation-only reprojection: "hot air"), and the resolution band sits at its floor 0.6 the
// whole session because every window misses. The MSAA cost on the Adreno tiler is mostly a FIXED
// tax (the full vertex shader re-runs per bin; 4x = the most bins): MSAA0 at 0.6 = 12.3 ms / 59–64
// fps, the only measured arm off half rate. At MSAA0 the pixel part is ~14 ms × scale² (ms0 at 1.0 =
// 21.3 ms), so a sharper scale does NOT come with it: the steady point is MSAA0 near 0.6–0.65.
//
//   samples   the multiview eye target's MSAA (xrmv.js, re-attached every frame: live)
//   res       the resolution band over CLASS_RES.quest (adaptres.js setResBand: live)
//   gm_lite   the ground's near fragment lite (groundmat.js GM_LITE: no detail / macro layer taps,
//             the hex tiling one tap; the colour, the class blend, the ways, the water stay) —
//             the ground programs recompile on a switch (one hitch; main.js switchQuestProfile)
//
// A CLIENT rule (renderconfig is a bake-id term). The pick is stored in localStorage (`geovr_qp`);
// the URL param wins and is stored too.
import { deviceClass } from './device.js';
import { vrsProfile } from './vrstyle/index.js';

export const QUEST_PROFILES = {
  current: { samples: 4, res: {}, gm_lite: false },
  balanced: { samples: 2, res: { home: 0.6, home_mv: 0.6, top: 0.8 }, gm_lite: true },
  steady: { samples: 0, res: { home: 0.65, home_mv: 0.65, top: 0.85 }, gm_lite: true },
};
export const QUEST_PROFILE_NAMES = Object.keys(QUEST_PROFILES);
// the default (w54, headset, VrApi, town view, 60 s cells): balanced (MSAA2 at 0.6) HOLDS the steady band — still
// 71 fps / App 10.7 ms vs steady 64 / 12.6, turning 59–62 vs 60–65 fps (App +0.4–0.8 ms) — the Adreno tiler's
// MSAA2 is near free, the pixel count decides (steady's 0.65 = +17 % pixels); MSAA2 cuts the geometry-edge
// "ants" 39–72 % (w53 seam lab). A stored pick (geovr_qp) keeps its choice.
export const QUEST_PROFILE_DEFAULT = 'balanced';
const LS_QP = 'geovr_qp';

let _name = null;          // the pick in force (null = not decided yet)

function lsGet(k) { try { return localStorage.getItem(k); } catch { return null; } }
function lsSet(k, v) { try { localStorage.setItem(k, String(v)); } catch { /* private mode */ } }

// the URL's ?qp (a known name) → stored; else the stored pick; else the default
export function pickQuestProfile(search = (typeof location !== 'undefined' ? location.search : ''), stored = lsGet(LS_QP)) {
  let u = null;
  try { u = new URLSearchParams(search || '').get('qp'); } catch { u = null; }
  if (u && QUEST_PROFILES[u]) { lsSet(LS_QP, u); return u; }
  return stored && QUEST_PROFILES[stored] ? stored : QUEST_PROFILE_DEFAULT;
}

// cls: the device class (default the decided one) → the profile's terms, or null (not the Quest)
export function questProfile(cls = deviceClass()) {
  if (cls !== 'quest') return null;
  const vs = vrsProfile(cls); if (vs) return vs;   // THE VR LOOK's terms (vrstyle/, APP_VRSTYLE)
  if (_name == null) _name = pickQuestProfile();
  return QUEST_PROFILES[_name];
}
export function questProfileName(cls = deviceClass()) { return questProfile(cls) ? _name : null; }

// the owner's switch (VR menu, main.js switchQuestProfile: the band + the ground programs follow; xrmv
// reads the samples per frame): stored
export function setQuestProfile(name) {
  if (!QUEST_PROFILES[name] || deviceClass() !== 'quest') return false;
  _name = name; lsSet(LS_QP, name);
  return true;
}

// SPACE WARP (spacewarp.js, w43): only while a session GRANTED 'space-warp' (setQuestSw from its begin / end)
// the app renders at half the display rate (~27 ms a frame at 72 Hz instead of ~13.9): the band and the eye MSAA
// come back over the picture profile's (the profile's own terms return at the session's end)
export const QUEST_SW = { samples: 4, res: { home: 0.85, home_mv: 0.85, top: 1 } };
let _sw = false;
export function setQuestSw(on) { _sw = !!on; }
export function questSw() { return _sw; }

// the multiview eye target's sample count under the profile (null: the caller's own default)
export function questSamples() { const p = questProfile(); return p ? (_sw ? QUEST_SW.samples : p.samples) : null; }
// the ground's near lite (groundmat.js)
export function questGmLite() { const p = questProfile(); return !!(p && p.gm_lite); }
// test seam (scripts/check-device.mjs): forget the pick
export function _resetQuestProfile(name = null) { _name = name; }
